import {
  Button,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Text,
  VStack,
  Spinner,
  Center,
  Box,
  Icon,
  Card as ChakraCard,
  CardBody,
} from '@chakra-ui/react'
import debounce from 'lodash.debounce'
import { FunctionComponent, useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import { RootState } from '../../app/store'
import Card from '../../common/components/Card'
import { patientsApi, useLazyGetPatientsQuery } from '../../services/patients'
import NewPatientModal from '../PatientsPage/NewPatientModal'
import { selectPatient } from '../../features/patient/PatientContextSlice'
import { setDeliveryState } from './ScriptForm/PrescribedItem/PrescribedItemSlice'
import { renderValidatedResult } from './jsxUtilities'
import { validateMedicareNumber } from '../../utils/validation-helpers/MedicareValidate'
import { validateIhi } from '../../utils/validation-helpers/IhiValidate'
import {
  displayIfPresent,
  validateDate,
  validateExists,
} from '../../utils/validation-helpers/Common'
import { Prescriber } from '../../types'
import { useLazyGetPatientByExternalIdQuery } from '../../services/patientsBFF'
import { LimitedPrescriberProfile } from '../../features/users/UsersSlice'
import { PrescriptionDeliveryMethod } from '../../types/prescription-delivery-method'
import ClientSSOContext from '../../features/sso/types/ClientSSOContext'
import {
  useGetAvailableIntegrationsQuery,
  useConnectIntegrationMutation,
} from '../../features/integration/integrationSlice'
import { OPAU_CLIENT_GROUP_NAME, SSO_CLIENT } from '../../features/sso/constant'
import { FiAlertCircle, FiRefreshCw } from 'react-icons/fi'
import { DVA_CARD_COLOUR } from '../PatientsPage/constants'
import { format, parseISO } from 'date-fns'
import { resetAllValidationResults } from '../PatientsPage/PatientFormSlice'
import { useHasEntitlement } from '../OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../types/organization-entitlements'
import { getPatientAccessScope } from '../../features/patient/patientAccessScope'

const IhiGenderToLabel = {
  M: 'Male',
  F: 'Female',
  I: 'Intersex or Indeterminate',
  N: 'Not State',
}
type IhiGender = keyof typeof IhiGenderToLabel

const LoadingState: FunctionComponent<Record<string, never>> = () => (
  <Center py={8}>
    <VStack spacing={6}>
      <Box
        bg="blue.50"
        border="1px solid"
        borderColor="blue.200"
        borderRadius="lg"
        p={6}
        textAlign="center"
        maxW="400px"
        w="100%"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="blue.700" fontWeight="medium" fontSize="md">
            Patient data being acquired...
          </Text>
        </VStack>
      </Box>
    </VStack>
  </Center>
)

const ErrorState: FunctionComponent<{
  error: boolean
  onSelectAnother: () => void
  canManagePatient: boolean
  errorMessage?: string
}> = ({ error, onSelectAnother, canManagePatient, errorMessage }) => {
  return error ? (
    <VStack align="center" py={4}>
      <Box
        bg="red.50"
        border="1px solid"
        borderColor="red.200"
        borderRadius="lg"
        p={6}
        textAlign="center"
        paddingY="8px"
        maxW="800px"
        w="90%"
      >
        <VStack spacing={4}>
          <Icon as={FiAlertCircle} w={8} h={8} color="red.500" />
          <Text color="red.700" fontWeight="medium" fontSize="md">
            {errorMessage || 'Failed to fetch patient data. Please try again.'}
          </Text>
          {canManagePatient && (
            <Button
              leftIcon={<Icon as={FiRefreshCw} />}
              colorScheme="blue"
              variant="outline"
              onClick={onSelectAnother}
              _hover={{
                bg: 'blue.50',
                borderColor: 'blue.300',
              }}
              size="md"
            >
              Select another patient
            </Button>
          )}
        </VStack>
      </Box>
    </VStack>
  ) : null
}

const PatientSearchResultItem: FunctionComponent<{ title: string; value: string }> = ({
  title,
  value,
}) => {
  return (
    <Box mb={{ base: 2, md: 0 }} _last={{ mb: 0 }}>
      <Text fontSize="sm">{title}</Text>
      <Text fontWeight="bold">{value}</Text>
    </Box>
  )
}

const PatientSearch: FunctionComponent<{
  searchQuery: string
  onSearchChange: (value: string) => void
  onSearch: () => void
  onSelectPatient: (patient: any) => void
  isDeactivated: boolean
  prescriber: Prescriber
  onAddPatient: (patient: any) => void
  results: any[]
  isSearching: boolean
}> = ({
  searchQuery,
  onSearchChange,
  onSearch,
  onSelectPatient,
  isDeactivated,
  prescriber,
  onAddPatient,
  results,
  isSearching,
}) => {
  const dispatch = useAppDispatch()
  return (
    <VStack alignItems="stretch">
      <Text fontWeight="bold" flexGrow="1">
        Select a patient
      </Text>
      <Input
        maxWidth="100%"
        bgColor="white"
        placeholder="Search Patient"
        disabled={isDeactivated}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onSearch()
          }
        }}
      />

      {searchQuery && (
        <VStack>
          {isSearching ? (
            <Spinner size="sm" />
          ) : results?.length > 0 ? (
            results.map((patient) => (
              <ChakraCard
                variant={'elevated'}
                key={patient.id}
                width="100%"
                cursor="pointer"
                _hover={{ backgroundColor: 'gray.100' }}
                onClick={() => onSelectPatient(patient)}
              >
                <CardBody>
                  <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4} width="100%">
                    <PatientSearchResultItem
                      title="Name"
                      value={`${patient.given_names} ${patient.family_name}`}
                    />
                    <PatientSearchResultItem
                      title="Date of Birth"
                      value={format(parseISO(patient.date_of_birth), 'd MMMM yyyy')}
                    />
                    <PatientSearchResultItem
                      title="Address"
                      value={`${patient.address_1}, ${patient.state} ${patient.postcode}`}
                    />
                  </SimpleGrid>
                </CardBody>
              </ChakraCard>
            ))
          ) : (
            <Text>No patients found</Text>
          )}
        </VStack>
      )}
      <NewPatientModal
        onAdd={onAddPatient}
        hidePrescriberSelection={true}
        selectedPrescriber={prescriber as LimitedPrescriberProfile}
      >
        {({ openModal }) => (
          <Button
            colorScheme="blue"
            onClick={() => {
              dispatch(resetAllValidationResults())
              openModal()
            }}
            disabled={isDeactivated}
          >
            New Patient
          </Button>
        )}
      </NewPatientModal>
    </VStack>
  )
}

const PatientDetailsPanel: FunctionComponent<unknown> = () => {
  const hasAttemptedFetch = useRef(false)
  const hasAttemptedIntegrationConnection = useRef(false)
  const [patientSearchQuery, setPatientSearchQuery] = useState('')
  const [showFetchPatientError, setShowFetchPatientError] = useState(false)
  const [isIntegrationConnected, setIsIntegrationConnected] = useState(false)

  const { data: availableIntegrations } = useGetAvailableIntegrationsQuery()
  const [connectIntegration] = useConnectIntegrationMutation()
  const isSharedPatientsFeatureOn = useHasEntitlement(FeatureKey.SHARED_PATIENTS)

  const [searchPatients, searchPatientsResult] = useLazyGetPatientsQuery()

  const prescriber = useAppSelector(({ prescriber }: RootState) => prescriber?.prescriber)
  const organizationSettings = useAppSelector((state) => state.organization?.data?.settings)
  const isAbleToManagePatient = organizationSettings?.enableManagePatient
  const isDeactivatedPrescriber = !prescriber?.active
  const defaultPrescriptionDeliveryMethod = organizationSettings?.defaultPrescriptionDeliveryMethod
  const selectedPatient = useSelector(
    ({ patientDetail }: RootState) => patientDetail.selectedPatient
  )

  const dispatch = useAppDispatch()
  const clientSSOContextDataString = localStorage.getItem('clientSSOContext')
  const clientSSOContextData: ClientSSOContext | null = clientSSOContextDataString
    ? JSON.parse(clientSSOContextDataString)
    : null
  const externalPatientFetchId = clientSSOContextData?.externalPatientFetchId
  const metadata = clientSSOContextData?.metadata
  const isManualIntegrationClient =
    clientSSOContextData?.clientName &&
    ([SSO_CLIENT.HDA, SSO_CLIENT.TELECARE].includes(clientSSOContextData?.clientName) ||
      clientSSOContextData?.clientName.startsWith(OPAU_CLIENT_GROUP_NAME))
  const providerId = clientSSOContextData?.providerId

  const [syncPatient, syncPatientResult] = useLazyGetPatientByExternalIdQuery()

  const isAbleToAutoBuildIntegrations =
    organizationSettings?.enableAutoBuildIntegration &&
    !isDeactivatedPrescriber &&
    prescriber?.entityId &&
    providerId

  const getErrorMessage = (error: any): string => {
    if (!error) return 'Failed to fetch patient data. Please try again.'

    const errorData = error.data || error
    const message = errorData?.message
    const statusCode = errorData?.statusCode

    if (message === 'Patient IHI is not found' && statusCode === 409) {
      return 'Patient IHI validation failed. Please verify the patient information and try again.'
    }

    if (message === 'Patient not found' && statusCode === 404) {
      return 'Patient not found in the system. Please check the patient details and try again.'
    }

    return 'Failed to fetch patient data. Please try again.'
  }

  useEffect(() => {
    const checkAndConnectIntegration = async () => {
      if (!availableIntegrations || !isAbleToAutoBuildIntegrations) {
        return
      }
      const errorKey = `integration_failed_${providerId}`
      const hasFailedBefore = localStorage.getItem(errorKey)
      if (hasFailedBefore) {
        hasAttemptedIntegrationConnection.current = true
        return
      }
      if (hasAttemptedIntegrationConnection.current) {
        return
      }

      const isProviderConnected = availableIntegrations.some(
        (integration) => integration.id === providerId && integration.connected
      )

      if (isProviderConnected) {
        setIsIntegrationConnected(true)
        hasAttemptedIntegrationConnection.current = true
        return
      }
      hasAttemptedIntegrationConnection.current = true

      try {
        await connectIntegration(providerId).unwrap()
        setIsIntegrationConnected(true)
      } catch (error: any) {
        console.error('Failed to connect integration:', error)
        localStorage.setItem(errorKey, 'true')
        setShowFetchPatientError(false)
      }
    }

    checkAndConnectIntegration()
  }, [providerId, availableIntegrations, isAbleToAutoBuildIntegrations])

  useEffect(() => {
    if (
      externalPatientFetchId &&
      !selectedPatient.id &&
      !hasAttemptedFetch.current &&
      (isIntegrationConnected || isManualIntegrationClient)
    ) {
      hasAttemptedFetch.current = true
      syncPatient({
        externalPatientFetchId,
        ...(providerId && { providerId }),
        metadata,
      })
    }
  }, [
    externalPatientFetchId,
    syncPatient,
    providerId,
    selectedPatient.id,
    isIntegrationConnected,
    isManualIntegrationClient,
  ])

  useEffect(() => {
    if (syncPatientResult.error) {
      setShowFetchPatientError(true)
    }
  }, [syncPatientResult.error])

  useEffect(() => {
    if (syncPatientResult.data) {
      dispatch(selectPatient(syncPatientResult.data))
    }
  }, [syncPatientResult.data, dispatch])

  useEffect(() => {
    dispatch(
      setDeliveryState({
        sendViaEmail: defaultPrescriptionDeliveryMethod === PrescriptionDeliveryMethod.Email,
        sendViaSMS: defaultPrescriptionDeliveryMethod === PrescriptionDeliveryMethod.SMS,
        printPaperToken:
          defaultPrescriptionDeliveryMethod === PrescriptionDeliveryMethod.Paper_Token,
        recipientEmail: selectedPatient?.email,
        recipientMobile: selectedPatient?.phone_number,
      })
    )
  }, [dispatch, selectedPatient, defaultPrescriptionDeliveryMethod])

  const fetchSearchResult = useCallback(() => {
    if (patientSearchQuery.length < 2) return
    const patientScope = getPatientAccessScope({
      isSharedPatientsEnabled: isSharedPatientsFeatureOn,
      prescriberId: prescriber?.id,
    })

    if (!patientScope) {
      return
    }

    searchPatients({
      searchTerm: patientSearchQuery,
      page: 1,
      limit: 10,
      ...patientScope,
    })
  }, [patientSearchQuery, isSharedPatientsFeatureOn, prescriber?.id, searchPatients])

  const fetchSearchResultRef = useRef(fetchSearchResult)
  fetchSearchResultRef.current = fetchSearchResult

  const debouncedFetch = useMemo(() => debounce(() => fetchSearchResultRef.current(), 1000), [])

  useEffect(() => {
    debouncedFetch()
    return () => debouncedFetch.cancel()
  }, [patientSearchQuery, debouncedFetch])

  const onSelectAnotherPatient = () => {
    dispatch(selectPatient(undefined))
    setPatientSearchQuery('')
    setShowFetchPatientError(false)
    hasAttemptedIntegrationConnection.current = false
    hasAttemptedFetch.current = false
    if (clientSSOContextData) {
      const updatedContext = {
        ...clientSSOContextData,
        externalPatientFetchId: undefined,
        referralId: undefined,
        metadata: undefined,
      }
      localStorage.setItem('clientSSOContext', JSON.stringify(updatedContext))
    }
    if (providerId) {
      const errorKey = `integration_failed_${providerId}`
      localStorage.removeItem(errorKey)
    }
  }

  const handleSelectPatient = (patient: any) => {
    dispatch(selectPatient(patient))
    dispatch(patientsApi.util.resetApiState())
    setPatientSearchQuery('')
  }

  return (
    <VStack alignItems="stretch">
      <Heading as="h4" size="md">
        Patient
      </Heading>
      <Card p="3">
        {syncPatientResult.isLoading ? (
          <LoadingState />
        ) : selectedPatient.id ? (
          <VStack flexGrow="1" flexBasis="0" alignItems="stretch">
            <HStack>
              <Text fontWeight="bold">IHI:</Text>
              {renderValidatedResult(
                validateIhi(selectedPatient.ihi_number, selectedPatient.ihi_status)
              )}
            </HStack>
            <HStack>
              <Text fontWeight="bold">Given Name:</Text>
              <Text>{selectedPatient.given_names}</Text>
            </HStack>
            <HStack>
              <Text fontWeight="bold">Family Name:</Text>
              <Text>{selectedPatient.family_name}</Text>
            </HStack>
            <HStack>
              <Text fontWeight="bold">Gender:</Text>
              <Text>{IhiGenderToLabel[selectedPatient.gender as IhiGender]}</Text>
            </HStack>
            <HStack>
              <Text fontWeight="bold">Date of Birth:</Text>
              {renderValidatedResult(
                validateDate(
                  selectedPatient.date_of_birth,
                  'Date of Birth',
                  "Please update this patient's Date of birth."
                )
              )}
            </HStack>
            {selectedPatient.dva_file_no ? (
              <HStack spacing={4}>
                <HStack>
                  <Text fontWeight="bold">DVA File Number:</Text>
                  <Text>{selectedPatient.dva_file_no}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold">DVA Card Colour:</Text>
                  <Text>{DVA_CARD_COLOUR[selectedPatient.dva_card_colour]}</Text>
                </HStack>
              </HStack>
            ) : (
              selectedPatient.medicare_no && (
                <HStack spacing={4}>
                  <HStack>
                    <Text fontWeight="bold">Medicare Number:</Text>
                    {renderValidatedResult(validateMedicareNumber(selectedPatient.medicare_no))}
                  </HStack>
                  <HStack>
                    <Text fontWeight="bold">IRN:</Text>
                    {renderValidatedResult(
                      validateExists(
                        selectedPatient.medicare_irn,
                        'Medicare IRN',
                        "Please update this patient's Medicare Individual Reference Number (IRN)."
                      )
                    )}
                  </HStack>
                </HStack>
              )
            )}
            <HStack>
              <Text fontWeight="bold">Address:</Text>
              <VStack align="left" spacing="1px">
                <HStack>
                  {renderValidatedResult(
                    validateExists(
                      selectedPatient.address_1,
                      'Patient Address Line 1',
                      'Please update patient address.'
                    )
                  )}
                </HStack>
                <HStack>
                  {renderValidatedResult(
                    displayIfPresent(
                      selectedPatient.address_2,
                      'Patient Address Line 2',
                      'Please update patient address.'
                    )
                  )}
                </HStack>
                <HStack>
                  {renderValidatedResult(
                    validateExists(
                      selectedPatient.suburb,
                      'Patient Suburb',
                      'Please update patient address.'
                    )
                  )}
                  {renderValidatedResult(
                    validateExists(
                      selectedPatient.postcode,
                      'Patient Postcode',
                      'Please update patient address.'
                    )
                  )}
                  {renderValidatedResult(
                    validateExists(
                      selectedPatient.state,
                      'Patient State',
                      'Please update patient address.'
                    )
                  )}
                </HStack>
              </VStack>
            </HStack>
            {isAbleToManagePatient && (
              <Button
                leftIcon={<Icon as={FiRefreshCw} />}
                colorScheme="blue"
                variant="outline"
                onClick={onSelectAnotherPatient}
                _hover={{
                  bg: 'blue.50',
                  borderColor: 'blue.300',
                }}
              >
                Select another patient
              </Button>
            )}
          </VStack>
        ) : !showFetchPatientError &&
          isAbleToManagePatient &&
          (!externalPatientFetchId || !isIntegrationConnected) ? (
          <PatientSearch
            searchQuery={patientSearchQuery}
            onSearchChange={setPatientSearchQuery}
            onSearch={fetchSearchResult}
            onSelectPatient={handleSelectPatient}
            isDeactivated={isDeactivatedPrescriber}
            prescriber={prescriber as Prescriber}
            onAddPatient={(patient) => dispatch(selectPatient(patient))}
            results={searchPatientsResult.data?.patients ?? []}
            isSearching={searchPatientsResult.isFetching}
          />
        ) : (
          <ErrorState
            error={!!syncPatientResult.error}
            onSelectAnother={onSelectAnotherPatient}
            canManagePatient={!!isAbleToManagePatient}
            errorMessage={getErrorMessage(syncPatientResult.error)}
          />
        )}
      </Card>
    </VStack>
  )
}

export default PatientDetailsPanel
